import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, LifeBuoy, Loader2, Lock, Mail, User as UserIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.3 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.3 29.6 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.6l-6.5-5.5c-2 1.4-4.6 2.3-7.6 2.3-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.5 39.5 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.5 5.5C42.6 35.4 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * AuthChoiceModal — handles three flows:
 *  - "login":    Google + email/password login
 *  - "register": pick role (Cliente / Professionista) -> Google or email/password
 *                Professionals must pick a business category (A/B/C/D).
 */
export default function AuthChoiceModal({ open, onOpenChange, initialMode = 'login', onSuccess }) {
  const { setUser } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  // step: 'login' | 'login-email' | 'reg-role' | 'reg-method' | 'reg-email'
  const [step, setStep] = useState('login');
  const [role, setRole] = useState('cliente'); // cliente | professionista
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(initialMode === 'register' ? 'reg-role' : 'login');
      setEmail('');
      setPassword('');
      setName('');
      setRole('cliente');
      setLoading(false);
    }
  }, [open, initialMode]);

  const close = (val) => onOpenChange(val);

  const goToGoogle = () => {
    if (role === 'professionista' || step.startsWith('reg-')) {
      sessionStorage.setItem('kyloo_login_intent', role === 'professionista' ? 'professional' : 'user');
    }
    const dest = role === 'professionista' ? '/dashboard' : '/home';
    const redirectUrl = window.location.origin + dest;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error(t('auth.err_missing_credentials'));
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: email.trim(), password },
        { withCredentials: true },
      );
      setUser(data);
      toast.success(t('auth.welcome_back', { name: data.name || data.email }));
      close(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, t('auth.err_generic')) || t('auth.err_login'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) return toast.error(t('auth.err_fill_all'));
    if (password.length < 8) return toast.error(t('auth.err_password_min'));
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email: email.trim(), password, name: name.trim() },
        { withCredentials: true },
      );
      setUser(data);
      if (role === 'professionista') {
        sessionStorage.setItem('kyloo_login_intent', 'professional');
      }
      toast.success(t('auth.welcome_new', { name: data.name }));
      close(false);
      onSuccess?.({ ...data, role });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, t('auth.err_generic')) || t('auth.err_register'));
    } finally {
      setLoading(false);
    }
  };

  // ============ View renderers ============

  const BackButton = ({ to }) => (
    <button
      onClick={() => setStep(to)}
      className="absolute left-4 top-4 text-slate-500 hover:text-slate-800"
      data-testid="auth-back-button"
      aria-label={t('common.back')}
    >
      <ArrowLeft className="w-4 h-4" />
    </button>
  );

  const GoogleButton = () => (
    <Button
      onClick={goToGoogle}
      className="w-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 py-6 rounded-xl flex items-center justify-center gap-3 shadow-sm"
      data-testid="auth-google-button"
    >
      <GoogleIcon />
      <span className="font-medium">{step === 'login' ? t('auth.google_login') : t('auth.google_continue')}</span>
    </Button>
  );

  const renderLogin = () => (
    <div className="space-y-3 pt-2" data-testid="auth-login-choice">
      <GoogleButton />
      <Button
        onClick={() => setStep('login-email')}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl flex items-center justify-center gap-3"
        data-testid="auth-email-login-button"
      >
        <Mail className="w-5 h-5" />
        <span className="font-medium">{t('auth.email_login_btn')}</span>
      </Button>
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={() => setStep('reg-role')}
          className="text-sm text-blue-600 hover:underline"
          data-testid="auth-switch-to-register"
        >
          {t('auth.switch_to_register')}
        </button>
      </div>
    </div>
  );

  const renderLoginEmail = () => (
    <form onSubmit={handleLogin} className="space-y-3 pt-2" data-testid="auth-login-form">
      <BackButton to="login" />
      <div>
        <Label className="text-sm text-slate-700">{t('auth.email')}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.it"
            className="mt-1 pl-9 rounded-xl bg-white border-slate-300" autoComplete="email" required
            data-testid="auth-login-email" />
        </div>
      </div>
      <div>
        <Label className="text-sm text-slate-700">{t('auth.password')}</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.password_placeholder')}
            className="mt-1 pl-9 rounded-xl bg-white border-slate-300" autoComplete="current-password" required
            data-testid="auth-login-password" />
        </div>
      </div>
      <Button type="submit" disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl"
        data-testid="auth-login-submit">
        {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {t('auth.login_submit')}
      </Button>
      <button
        type="button"
        onClick={() => {
          onOpenChange(false);
          navigate('/assistenza');
        }}
        className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-blue-700 text-sm font-medium pt-2 transition-colors"
        data-testid="auth-need-help"
      >
        <LifeBuoy className="w-4 h-4" />
        Ti serve aiuto?
      </button>
    </form>
  );

  const renderRegRole = () => (
    <div className="space-y-3 pt-2" data-testid="auth-role-choice">
      <button
        type="button"
        onClick={() => { setRole('cliente'); setStep('reg-method'); }}
        className="w-full p-4 rounded-2xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left flex items-center gap-3"
        data-testid="auth-role-cliente"
      >
        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
          <UserIcon className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <p className="font-semibold text-slate-800">{t('auth.role_client')}</p>
          <p className="text-xs text-slate-500">{t('auth.role_client_desc')}</p>
        </div>
      </button>
      <button
        type="button"
        onClick={() => {
          setRole('professionista');
          // Atomic flow: close the modal and navigate to the dedicated signup page
          // where the user enters credentials + shop data in one shot.
          close(false);
          navigate('/registrazione-attivita');
        }}
        className="w-full p-4 rounded-2xl border-2 border-slate-200 hover:border-orange-500 hover:bg-orange-50 transition-all text-left flex items-center gap-3"
        data-testid="auth-role-professionista"
      >
        <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
          <Briefcase className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <p className="font-semibold text-slate-800">{t('auth.role_pro')}</p>
          <p className="text-xs text-slate-500">{t('auth.role_pro_desc')}</p>
        </div>
      </button>
      <div className="text-center pt-2">
        <button
          type="button"
          onClick={() => setStep('login')}
          className="text-sm text-blue-600 hover:underline"
          data-testid="auth-switch-to-login"
        >
          {t('auth.switch_to_login')}
        </button>
      </div>
    </div>
  );

  const renderRegMethod = () => (
    <div className="space-y-3 pt-2" data-testid="auth-reg-method">
      <BackButton to="reg-role" />
      <p className="text-sm text-center text-slate-600">
        {role === 'professionista' ? t('auth.registering_as_pro') : t('auth.registering_as_client')}
      </p>

      {/* Clients keep the Google one-click flow. Professionals register only with email+password. */}
      {role !== 'professionista' && <GoogleButton />}

      <Button
        onClick={() => setStep('reg-email')}
        variant={role === 'professionista' ? 'default' : 'outline'}
        className={
          role === 'professionista'
            ? 'w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl flex items-center justify-center gap-3'
            : 'w-full border-blue-600 text-blue-600 hover:bg-blue-50 py-6 rounded-xl flex items-center justify-center gap-3'
        }
        data-testid="auth-email-register-button"
      >
        <Mail className="w-5 h-5" />
        <span className="font-medium">{t('auth.email_register_btn')}</span>
      </Button>
    </div>
  );

  const renderRegEmail = () => (
    <form onSubmit={handleRegister} className="space-y-3 pt-2" data-testid="auth-register-form">
      <BackButton to={role === 'professionista' ? 'reg-role' : 'reg-method'} />
      <div>
        <Label className="text-sm text-slate-700">{role === 'professionista' ? t('auth.shop_name') : t('auth.name')}</Label>
        <div className="relative">
          <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={role === 'professionista' ? 'es. Calzature Mario' : 'Mario Rossi'}
            className="mt-1 pl-9 rounded-xl bg-white border-slate-300" autoComplete="name" required
            data-testid="auth-register-name" />
        </div>
      </div>
      <div>
        <Label className="text-sm text-slate-700">{t('auth.email')}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.it"
            className="mt-1 pl-9 rounded-xl bg-white border-slate-300" autoComplete="email" required
            data-testid="auth-register-email" />
        </div>
      </div>
      <div>
        <Label className="text-sm text-slate-700">{t('auth.password')}</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.password_min')} className="mt-1 pl-9 rounded-xl bg-white border-slate-300"
            autoComplete="new-password" minLength={8} required data-testid="auth-register-password" />
        </div>
      </div>
      <Button type="submit" disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl"
        data-testid="auth-register-submit">
        {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {t('auth.register_submit')}
      </Button>
    </form>
  );

  const titles = {
    login: { t: t('auth.login_title'), d: t('auth.login_subtitle') },
    'login-email': { t: t('auth.login_email_title'), d: t('auth.login_email_subtitle') },
    'reg-role': { t: t('auth.reg_role_title'), d: t('auth.reg_role_subtitle') },
    'reg-method': {
      t: role === 'professionista' ? t('auth.reg_pro_title') : t('auth.reg_client_title'),
      d: t('auth.reg_method_subtitle'),
    },
    'reg-email': { t: t('auth.reg_email_title'), d: t('auth.reg_email_subtitle') },
  };
  const cur = titles[step] || titles.login;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl text-slate-800 text-center">{cur.t}</DialogTitle>
          <DialogDescription className="text-slate-600 text-center">{cur.d}</DialogDescription>
        </DialogHeader>

        {step === 'login' && renderLogin()}
        {step === 'login-email' && renderLoginEmail()}
        {step === 'reg-role' && renderRegRole()}
        {step === 'reg-method' && renderRegMethod()}
        {step === 'reg-email' && renderRegEmail()}
      </DialogContent>
    </Dialog>
  );
}
